import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FilePdf, FileXls, ArrowLeft, Pencil, Receipt, Coins, Storefront,
  Package, ChartLine, Notepad, Tag, ChefHat,
  Percent, Truck, Bank,
} from "@phosphor-icons/react";

import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { api, API, fmtINR } from "../lib/kostra";
import { useAuth } from "../lib/auth";

const TOKEN_KEY = "kostra:token";

export default function MenuPreviewPage() {
  const { id } = useParams();
  const { isAdmin, openLogin } = useAuth();
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) { setLoading(false); return; }
    api.get(`/menus/${id}`)
      .then(({ data }) => setMenu(data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Menu not found"))
      .finally(() => setLoading(false));
  }, [id, isAdmin]);

  const grouped = useMemo(() => {
    if (!menu) return [];
    const by = new Map();
    const order = [];
    for (const it of (menu.items || [])) {
      const cat = (it.category || "").trim() || "Uncategorized";
      if (!by.has(cat)) { by.set(cat, []); order.push(cat); }
      by.get(cat).push(it);
    }
    return order.map((c) => ({ category: c, items: by.get(c) }));
  }, [menu]);

  const downloadExport = async (fmt) => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const resp = await fetch(`${API}/menus/${id}/export/${fmt}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error("Export failed");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `KOSTRA_Menu_${(menu?.name || "menu").replace(/[^\w-]+/g, "_")}.${fmt === "pdf" ? "pdf" : "xlsx"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error("Download failed"); }
  };

  // Guest CTA
  if (!isAdmin) {
    return (
      <div className="app-shell">
        <Header />
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 text-center">
          <h1 className="font-display text-3xl text-[#0A3B24]">Menu Preview is admin-only</h1>
          <p className="text-sm text-[#4B5563] mt-2">Log in to view this menu.</p>
          <Button onClick={() => openLogin()}
                  className="mt-5 bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold h-11 px-6"
                  data-testid="preview-guest-login-btn">
            Admin login
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="app-shell">
        <Header />
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-20 text-center text-[#4B5563]">
          <div className="inline-flex items-center gap-2">
            <span className="h-3 w-3 rounded-full bg-[#D4AF37] animate-ping" />
            <span className="font-semibold text-[#0A3B24]">Loading menu…</span>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!menu) {
    return (
      <div className="app-shell">
        <Header />
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-16 text-center">
          <h1 className="font-display text-2xl text-[#0A3B24]">Menu not found</h1>
          <p className="text-sm text-[#4B5563] mt-2">It may have been deleted.</p>
          <Link to="/menu">
            <Button className="mt-4 bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold">
              <ArrowLeft size={14} weight="bold" className="mr-1.5" /> Back to Menu Creator
            </Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const totals = menu.totals || {};

  return (
    <div className="app-shell" data-testid="menu-preview-page">
      <Header />

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#0A3B24] to-[#0E5232] text-white">
        <div className="max-w-5xl mx-auto px-5 md:px-8 pt-10 pb-14">
          <Link to="/menu" className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[#D4AF37] hover:text-white font-semibold mb-4"
                data-testid="preview-back-link">
            <ArrowLeft size={12} weight="bold" /> Back to Menu Creator
          </Link>
          <div className="text-[#D4AF37] text-xs tracking-[0.25em] uppercase font-semibold inline-flex items-center gap-2">
            <Notepad size={13} weight="fill" /> Menu Preview
          </div>
          <h1 className="font-display text-3xl md:text-5xl font-medium mt-2 leading-tight" data-testid="preview-menu-name">
            {menu.name}
          </h1>
          {menu.outlet && (
            <div className="text-white/80 text-base mt-1" data-testid="preview-menu-outlet">
              <Storefront size={13} weight="bold" className="inline mr-1.5" />
              {menu.outlet}
            </div>
          )}
          {menu.notes && (
            <p className="text-white/70 text-sm mt-3 max-w-3xl whitespace-pre-line">{menu.notes}</p>
          )}

          {/* KPI strip */}
          <div className="mt-7 grid grid-cols-2 md:grid-cols-6 gap-3">
            <KPI label="Items" value={totals.item_count || 0} />
            <KPI label="Food Cost Σ" value={fmtINR(totals.food_cost_total)} />
            <KPI label="Listed SP Σ" value={fmtINR(totals.selling_price_total)} gold />
            <KPI label="Net Sale Σ" value={fmtINR(totals.net_sale_price_total || totals.selling_price_total)} gold />
            <KPI label="GP Swiggy Σ" value={fmtINR(totals.gross_profit_swiggy_total ?? totals.gross_profit_total)} ok />
            <KPI label="GP Zomato Σ" value={fmtINR(totals.gross_profit_zomato_total ?? totals.gross_profit_total)} ok />
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Link to="/menu">
              <Button variant="outline"
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20 hover:border-white/40 font-semibold"
                      data-testid="preview-edit-btn">
                <Pencil size={14} weight="bold" className="mr-1.5" /> Edit in Menu Creator
              </Button>
            </Link>
            <Button onClick={() => downloadExport("pdf")} variant="outline"
                    className="bg-white text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold"
                    data-testid="preview-pdf-btn">
              <FilePdf size={14} weight="bold" className="mr-1.5" /> Download PDF
            </Button>
            <Button onClick={() => downloadExport("excel")}
                    className="bg-[#D4AF37] hover:bg-[#C5A028] text-[#0A3B24] font-semibold"
                    data-testid="preview-excel-btn">
              <FileXls size={14} weight="bold" className="mr-1.5" /> Download Excel
            </Button>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-5 md:px-8 -mt-8 pb-16 space-y-8">
        {grouped.length === 0 && (
          <div className="kostra-card p-10 text-center text-[#4B5563]">
            This menu has no items yet.
          </div>
        )}

        {grouped.map((sec) => (
          <section key={sec.category} className="kostra-card p-6" data-testid={`preview-section-${sec.category.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-center justify-between mb-4 pb-3 border-b-2 border-[#D4AF37]/40">
              <h2 className="font-display text-xl text-[#0A3B24] inline-flex items-center gap-2">
                <Tag size={16} weight="duotone" className="text-[#D4AF37]" />
                {sec.category}
              </h2>
              <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">
                {sec.items.length} item{sec.items.length === 1 ? "" : "s"}
              </div>
            </div>

            <div className="space-y-3">
              {sec.items.map((it, i) => {
                const p = it.pricing || {};
                return (
                  <div key={it.id || `${sec.category}-${i}`}
                       className="rounded-xl border-2 border-[#E5E7EB] bg-white p-4 hover:border-[#D4AF37]/50 transition"
                       data-testid={`preview-item-${it.id || i}`}>
                    {/* Title row */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="font-display text-lg text-[#0A3B24] leading-tight inline-flex items-center gap-2">
                          <ChefHat size={14} weight="duotone" className="text-[#D4AF37]" />
                          {it.dish_name}
                          {!it.recipe_found && (
                            <Badge variant="outline" className="border-[#DC2626] text-[#DC2626] text-[10px]">
                              Recipe deleted
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">Net SP (customer)</div>
                        <div className="font-display text-2xl font-bold text-[#A87C0E] font-mono-num leading-none">
                          ₹{Number(p.net_selling_price ?? p.selling_price ?? 0).toFixed(2)}
                        </div>
                        {(p.discount_amount > 0 || p.delivery_fee > 0 || p.gst_amount > 0) && (
                          <div className="text-[10px] text-[#4B5563] mt-1 font-mono-num">
                            Listed ₹{Number(p.selling_price || 0).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Cost breakdown */}
                    <div className="mt-4 grid grid-cols-2 md:grid-cols-6 gap-2">
                      <Stat icon={<Coins size={12} weight="bold" />} label="Food Cost"
                            value={`₹${Number(p.food_cost_per_portion || 0).toFixed(2)}`} />
                      <Stat icon={<ChartLine size={12} weight="bold" />}
                            label={`Markup${it.markup_mode === "percent" ? ` (${(it.markup_value || 0).toFixed(0)}%)` : ""}`}
                            value={`₹${Number(p.markup_amount || 0).toFixed(2)}`} />
                      <Stat icon={<Package size={12} weight="bold" />} label="Packaging"
                            value={`₹${Number(p.packaging_per_portion || 0).toFixed(2)}`} />
                      <Stat icon={<Storefront size={12} weight="bold" />}
                            label={`Swiggy${it.swiggy_fee_mode === "percent" ? ` (${(it.swiggy_fee_value || 0).toFixed(0)}%)` : ""}`}
                            value={`₹${Number(p.swiggy_fee_amount || 0).toFixed(2)}`} />
                      <Stat icon={<Receipt size={12} weight="bold" />} label="GP Swiggy"
                            value={`₹${Number(p.gross_profit_swiggy ?? p.gross_profit_per_portion ?? 0).toFixed(2)}`} accent="ok" />
                      <Stat icon={<Receipt size={12} weight="bold" />} label="GP Zomato"
                            value={`₹${Number(p.gross_profit_zomato ?? p.gross_profit_per_portion ?? 0).toFixed(2)}`} accent="ok" />
                    </div>
                    <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                      <Stat icon={<Storefront size={12} weight="bold" />}
                            label={`Zomato${it.zomato_fee_mode === "percent" ? ` (${(it.zomato_fee_value || 0).toFixed(0)}%)` : ""}`}
                            value={`₹${Number(p.zomato_fee_amount || 0).toFixed(2)}`} />
                      <Stat icon={<Receipt size={12} weight="bold" />} label="Net Sale Price"
                            value={`₹${Number(p.net_sale_price ?? (p.selling_price - (p.discount_amount || 0))).toFixed(2)}`} accent="gold" />
                    </div>

                    {/* Online sale modifiers row */}
                    {(p.discount_amount > 0 || p.delivery_fee > 0 || p.gst_amount > 0) && (
                      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                        <Stat icon={<Percent size={12} weight="bold" />}
                              label={`Discount${it.discount_mode === "percent" ? ` (${(it.discount_value || 0).toFixed(0)}%)` : ""}`}
                              value={`−₹${Number(p.discount_amount || 0).toFixed(2)}`} />
                        <Stat icon={<Truck size={12} weight="bold" />} label="Delivery"
                              value={`+₹${Number(p.delivery_fee || 0).toFixed(2)}`} />
                        <Stat icon={<Bank size={12} weight="bold" />}
                              label={`GST (${(it.gst_pct || 0).toFixed(1)}%)`}
                              value={`+₹${Number(p.gst_amount || 0).toFixed(2)}`} />
                        <Stat icon={<Receipt size={12} weight="bold" />} label="Net SP"
                              value={`₹${Number(p.net_selling_price || 0).toFixed(2)}`} accent="gold" />
                      </div>
                    )}

                    {/* Margin health */}
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <div className="inline-flex items-center gap-2 text-[#4B5563]">
                        <span className="font-semibold uppercase tracking-wider text-[10px]">Food Cost %</span>
                        <span className={`font-mono-num font-bold ${
                          (p.food_cost_pct || 0) > 40 ? "text-amber-700"
                            : (p.food_cost_pct || 0) > 30 ? "text-[#0A3B24]"
                              : "text-emerald-700"}`}>
                          {(p.food_cost_pct || 0).toFixed(1)}%
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-bold uppercase tracking-wider ${
                          (p.food_cost_pct || 0) > 40
                            ? "border-amber-500 text-amber-700 bg-amber-50"
                            : (p.food_cost_pct || 0) > 30
                              ? "border-[#0A3B24]/40 text-[#0A3B24] bg-[#FDF9EE]"
                              : "border-emerald-500 text-emerald-700 bg-emerald-50"}`}>
                          {(p.food_cost_pct || 0) > 40 ? "Watch" : (p.food_cost_pct || 0) > 30 ? "OK" : "Healthy"}
                        </span>
                      </div>
                    </div>

                    {/* Market & Bulk pricing — show every saved input field */}
                    {(Number(it.best_market_price) > 0 ||
                      Number(it.min_portions_per_batch) > 0 ||
                      Number(it.bulk_order_quantity) > 0) && (
                      <div className="mt-3 rounded-lg border border-dashed border-[#D4AF37]/40 bg-[#FDF9EE]/40 p-3">
                        <div className="text-[10px] uppercase tracking-wider text-[#A87C0E] font-bold mb-2 inline-flex items-center gap-1">
                          <ChartLine size={11} weight="bold" /> Market & Bulk Pricing
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {Number(it.best_market_price) > 0 && (
                            <Stat icon={<Tag size={12} weight="bold" />} label="Best Market Price"
                                  value={`₹${Number(it.best_market_price).toFixed(2)}`}
                                  accent={Number(p.selling_price) <= Number(it.best_market_price) ? "ok" : "warn"} />
                          )}
                          {Number(it.min_portions_per_batch) > 0 && (
                            <Stat icon={<Package size={12} weight="bold" />} label="Min Portions / Batch"
                                  value={`${Number(it.min_portions_per_batch).toFixed(0)}`} />
                          )}
                          {Number(it.bulk_order_quantity) > 0 && (
                            <Stat icon={<Package size={12} weight="bold" />} label="Bulk Order Qty"
                                  value={`${Number(it.bulk_order_quantity).toFixed(0)}`} />
                          )}
                        </div>
                        {/* Derived: Per Batch Cost · Bulk Order Rate · Bulk Pack Price · GP on Bulk Order */}
                        {(Number(p.bulk_order_rate) > 0 || Number(p.bulk_pack_price) > 0 || Number(p.per_batch_cost) > 0) && (
                          <div className="mt-2 pt-2 border-t border-[#D4AF37]/30 grid grid-cols-1 md:grid-cols-4 gap-2">
                            <Stat icon={<Coins size={12} weight="bold" />} label="Per Batch Cost"
                                  value={`₹${Number(p.per_batch_cost || 0).toFixed(2)}`} />
                            <Stat icon={<Tag size={12} weight="bold" />} label="Bulk Order Rate"
                                  value={`₹${Number(p.bulk_order_rate || 0).toFixed(2)}`} accent="gold" />
                            <Stat icon={<Receipt size={12} weight="bold" />} label="Bulk Pack Price"
                                  value={`₹${Number(p.bulk_pack_price || 0).toFixed(2)}`} accent="gold" />
                            <Stat icon={<Receipt size={12} weight="bold" />} label="GP on Bulk Order Qty"
                                  value={`₹${Number(p.gp_on_bulk_order || 0).toFixed(2)}`}
                                  accent={Number(p.gp_on_bulk_order || 0) >= 0 ? "ok" : "warn"} />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Section subtotal */}
              <SectionSubtotal items={sec.items} />
            </div>
          </section>
        ))}

        {/* Grand total */}
        <div className="kostra-card p-6 bg-gradient-to-br from-[#FDF9EE] to-white border-2 border-[#D4AF37]/40">
          <div className="font-display text-lg text-[#0A3B24] mb-3">Menu Totals</div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="preview-totals">
            <BigStat label="Items" value={totals.item_count || 0} />
            <BigStat label="Food Cost Σ" value={fmtINR(totals.food_cost_total)} />
            <BigStat label="Listed SP Σ" value={fmtINR(totals.selling_price_total)} gold />
            <BigStat label="Net Sale Σ" value={fmtINR(totals.net_sale_price_total || totals.selling_price_total)} gold />
            <BigStat label="GP Swiggy Σ" value={fmtINR(totals.gross_profit_swiggy_total ?? totals.gross_profit_total)} ok />
            <BigStat label="GP Zomato Σ" value={fmtINR(totals.gross_profit_zomato_total ?? totals.gross_profit_total)} ok />
          </div>
          {(totals.discount_total > 0 || totals.delivery_total > 0 || totals.gst_total > 0) && (
            <div className="mt-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-xs text-[#4B5563]">
              {totals.discount_total > 0 && (
                <div className="rounded-md bg-white border border-[#E5E7EB] px-3 py-1.5">
                  <span className="font-bold uppercase tracking-wider text-[10px]">Discount Σ</span>
                  <span className="font-mono-num ml-2 text-[#A87C0E]">−{fmtINR(totals.discount_total)}</span>
                </div>
              )}
              {totals.delivery_total > 0 && (
                <div className="rounded-md bg-white border border-[#E5E7EB] px-3 py-1.5">
                  <span className="font-bold uppercase tracking-wider text-[10px]">Delivery Σ</span>
                  <span className="font-mono-num ml-2 text-[#0A3B24]">+{fmtINR(totals.delivery_total)}</span>
                </div>
              )}
              {totals.gst_total > 0 && (
                <div className="rounded-md bg-white border border-[#E5E7EB] px-3 py-1.5">
                  <span className="font-bold uppercase tracking-wider text-[10px]">GST Σ</span>
                  <span className="font-mono-num ml-2 text-[#0A3B24]">+{fmtINR(totals.gst_total)}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

function KPI({ label, value, gold, ok }) {
  const color = gold ? "text-[#D4AF37]" : ok ? "text-emerald-300" : "text-white";
  return (
    <div className="rounded-lg bg-white/10 border border-white/20 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-white/60 font-bold">{label}</div>
      <div className={`mt-0.5 font-mono-num font-bold text-base ${color}`}>{value}</div>
    </div>
  );
}

function Stat({ icon, label, value, accent }) {
  const color = accent === "ok" ? "text-emerald-700"
    : accent === "gold" ? "text-[#A87C0E]"
    : accent === "warn" ? "text-amber-700"
    : "text-[#0A3B24]";
  return (
    <div className="rounded-md bg-[#FDF9EE]/40 border border-[#D4AF37]/30 p-2">
      <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold inline-flex items-center gap-1">
        {icon} {label}
      </div>
      <div className={`font-mono-num font-bold text-sm mt-0.5 ${color}`}>{value}</div>
    </div>
  );
}

function BigStat({ label, value, gold, ok }) {
  const color = gold ? "text-[#A87C0E]" : ok ? "text-emerald-700" : "text-[#0A3B24]";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-[#4B5563] font-bold">{label}</div>
      <div className={`mt-0.5 font-mono-num font-bold text-base ${color}`}>{value}</div>
    </div>
  );
}

function SectionSubtotal({ items }) {
  const sub = items.reduce((acc, it) => {
    const p = it.pricing || {};
    acc.food += Number(p.food_cost_per_portion || 0);
    acc.sp += Number(p.selling_price || 0);
    acc.nsp += Number(p.net_sale_price ?? (p.selling_price - (p.discount_amount || 0)) ?? 0);
    acc.gpS += Number(p.gross_profit_swiggy ?? p.gross_profit_per_portion ?? 0);
    acc.gpZ += Number(p.gross_profit_zomato ?? p.gross_profit_per_portion ?? 0);
    return acc;
  }, { food: 0, sp: 0, nsp: 0, gpS: 0, gpZ: 0 });
  return (
    <div className="rounded-lg bg-[#FDF9EE]/60 border border-[#D4AF37]/30 p-3 mt-2 flex flex-wrap items-center justify-between gap-3">
      <div className="text-xs font-semibold text-[#0A3B24] uppercase tracking-wider">Section subtotal</div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        <span className="text-[#4B5563]">Food: <span className="font-mono-num font-bold text-[#0A3B24]">{fmtINR(sub.food)}</span></span>
        <span className="text-[#4B5563]">Listed SP: <span className="font-mono-num font-bold text-[#A87C0E]">{fmtINR(sub.sp)}</span></span>
        <span className="text-[#4B5563]">Net Sale: <span className="font-mono-num font-bold text-[#A87C0E]">{fmtINR(sub.nsp)}</span></span>
        <span className="text-[#4B5563]">GP Swiggy: <span className="font-mono-num font-bold text-emerald-700">{fmtINR(sub.gpS)}</span></span>
        <span className="text-[#4B5563]">GP Zomato: <span className="font-mono-num font-bold text-emerald-700">{fmtINR(sub.gpZ)}</span></span>
      </div>
    </div>
  );
}
